import { useTranslation } from 'react-i18next';

import Checkbox from '@/components/atoms/Checkbox';
import {
    AUDIT_VIEW_ACTION,
    type AuditLogFilters,
    addAuditFilters,
    applyAuditFilterChange,
    areAuditViewsHidden,
} from '@/domain/auditLog';

import FilterTextField from '../../FilterTextField';
import ActionPicker from '../ActionPicker';
import FilterMenu from '../FilterMenu';
import type { AuditFilterBindings } from '../filterBindings';
import { entityTypeSections } from '../filterOptions';

import FilterGroup from './FilterGroup';
import PanelField from './PanelField';

import styles from './panelFields.module.scss';

interface WhatFiltersProps {
    bindings: AuditFilterBindings;
}

/** Hiding views also unticks them in Actions, which would otherwise lift the exclusion again. */
function withViewsHidden(filters: AuditLogFilters, hidden: boolean): AuditLogFilters {
    const others = (filters.excludeActions ?? []).filter((action) => action !== AUDIT_VIEW_ACTION);
    return {
        ...filters,
        excludeActions: hidden ? [AUDIT_VIEW_ACTION, ...others] : others,
        actions: hidden
            ? filters.actions?.filter((action) => action !== AUDIT_VIEW_ACTION)
            : filters.actions,
    };
}

export default function WhatFilters({ bindings }: WhatFiltersProps) {
    const { filters, facets, onChange, onType } = bindings;
    const { t } = useTranslation();
    const oneTargetType = filters.entityType?.length === 1;

    return (
        <FilterGroup legend={t('auditLog.filters.group.what')}>
            <PanelField label={t('auditLog.filters.actions')}>
                {(labelId) => (
                    <ActionPicker
                        bindings={bindings}
                        label={t('auditLog.filters.actions')}
                        labelledBy={labelId}
                    />
                )}
            </PanelField>
            <FilterTextField
                field="action"
                label={t('auditLog.filters.action')}
                filters={filters}
                onType={onType}
            />
            <label className={styles.checkbox}>
                <Checkbox
                    checked={areAuditViewsHidden(filters)}
                    onCheckedChange={(checked) => onChange(withViewsHidden(filters, checked))}
                />
                {t('auditLog.filters.hideViews')}
            </label>
            <PanelField label={t('auditLog.filters.entityType')}>
                {(labelId) => (
                    <FilterMenu
                        label={t('auditLog.filters.entityType')}
                        labelledBy={labelId}
                        options={entityTypeSections(
                            facets.entityTypes,
                            filters.entityType ?? [],
                            t
                        )}
                        selected={filters.entityType}
                        onChange={(entityType) =>
                            onChange(
                                applyAuditFilterChange(filters, addAuditFilters({ entityType }))
                            )
                        }
                        searchable
                    />
                )}
            </PanelField>
            <FilterTextField
                field="entityId"
                label={t('auditLog.filters.entityId')}
                filters={filters}
                onType={onType}
                // Without its type an id matches across entity kinds, and the query is slow.
                disabled={!oneTargetType}
                hint={oneTargetType ? undefined : t('auditLog.filters.entityIdHint')}
            />
            <FilterTextField
                field="entityLabel"
                label={t('auditLog.filters.entityLabel')}
                placeholder={t('auditLog.filters.containsPlaceholder')}
                filters={filters}
                onType={onType}
            />
        </FilterGroup>
    );
}
